import { clientInstance } from '../../../services/graphql.service';

import { changeTestDriveBooking } from '../../../graphql/mutations';
import {
  testDriveBookingsCalendar,
  testDriveBookingsTimeslots,
  userTestDriveBookings,
  userTestDriveRequests,
} from '../../../graphql/queries';

import {
  MutationCreateTestDriveBookingRescheduleArgs,
  QueryBookingTimeSlotsArgs,
  QueryTestDriveBookingsCalendarArgs,
  QueryUserTestDriveBookingsArgs,
  QueryUserTestDriveRequestsArgs,
  TestDriveBookingRescheduleInput,
} from '../../../graphql/schema';
import { CONTEXT } from '../../../models/constants';
import {
  ScheduledTestDrivesProps,
  TestDriveBookingsCalendarProps,
  TestDriveBookingsTimeslotsProps,
} from '../../../models/store/actions/scheduled-test-drives-props.model';
import GenericStore from '../../../models/store/generic-store.model';

import DateService from '../../../services/date.service';
import EventService from '../../../services/event.service';
import UtilsService from '../../../services/utils.service';

const { client } = clientInstance();

/* ==================== ACTIONS ==================== */
const initUserTestDrives = async (
  { dispatch }: GenericStore,
  props: ScheduledTestDrivesProps
): Promise<void> => {
  await dispatch('getUserTestDriveBookings', props);
  await dispatch('getUserTestDriveRequests', props);
};

const getTestDriveBookingsCalendar = async (
  { commit }: GenericStore,
  {
    dealerId,
    vehicleId,
    startDate,
    endDate,
    countryCode,
    language,
  }: TestDriveBookingsCalendarProps
): Promise<void> => {
  const variables: QueryTestDriveBookingsCalendarArgs = {
    dealerId,
    vehicleId,
    startDate,
    endDate,
    context: CONTEXT,
    countryCode,
    language,
  };

  await client
    .query(testDriveBookingsCalendar, variables)
    .toPromise()
    .then(async (value) => {
      if (value.data?.testDriveBookingsCalendar) {
        await commit(
          'testDriveBookingsCalendar',
          UtilsService.cleanTypenames(value.data.testDriveBookingsCalendar)
        );
      } else {
        await commit('testDriveBookingsCalendar', undefined);
      }
    });
};

const getTestDriveBookingsTimeSlots = async (
  { commit }: GenericStore,
  {
    dealerId,
    selectedDay,
    vehicleId,
    countryCode,
    language,
    onCompleted,
  }: TestDriveBookingsTimeslotsProps
): Promise<boolean> => {
  const variables: QueryBookingTimeSlotsArgs = {
    dealerId,
    countryCode,
    context: CONTEXT,
    language,
    vehicleId,
    day: DateService.formatDateForVBS(selectedDay),
  };

  let timeSlotsFound = false;

  await client
    .query(testDriveBookingsTimeslots, variables)
    .toPromise()
    .then(async (value) => {
      if (
        value.data?.testDriveBookingsTimeslots.timeSlotInterval &&
        value.data?.testDriveBookingsTimeslots.timeSlotInterval.length > 0
      ) {
        await commit(
          'testDriveBookingsTimeSlots',
          UtilsService.cleanTypenames(
            value.data.testDriveBookingsTimeslots.timeSlotInterval
          )
        );
        if (onCompleted) onCompleted();
        timeSlotsFound = true;
      } else {
        await commit('testDriveBookingsTimeSlots', []);
      }
    });

  return timeSlotsFound;
};

const getUserTestDriveBookings = async (
  { commit }: GenericStore,
  {
    vehicleTypes,
    modelSeriesId,
    countryCode,
    language,
    emit,
  }: ScheduledTestDrivesProps
): Promise<void> => {
  const variables: QueryUserTestDriveBookingsArgs = {
    startDateTime: '',
    vehicleTypes,
    countryCode,
    context: CONTEXT,
    language,
    modelSeriesId,
  };

  client
    .query(userTestDriveBookings, variables)
    .toPromise()
    .then((value) => {
      if (value.data?.userTestDriveBookings) {
        commit(
          'userTestDriveBookings',
          UtilsService.cleanTypenames(value.data.userTestDriveBookings)
        );
      } else {
        commit('userTestDriveBookings', []);
        EventService.emitRequestError(emit, value.error);
      }
    });
};

const getUserTestDriveRequests = async (
  { commit }: GenericStore,
  {
    vehicleTypes,
    modelSeriesId,
    countryCode,
    language,
    emit,
  }: ScheduledTestDrivesProps
): Promise<void> => {
  const variables: QueryUserTestDriveRequestsArgs = {
    startDateTime: '',
    vehicleTypes,
    countryCode,
    context: CONTEXT,
    language,
    modelSeriesId,
  };

  client
    .query(userTestDriveRequests, variables)
    .toPromise()
    .then((value) => {
      if (value.data?.userTestDriveRequests) {
        commit(
          'userTestDriveRequests',
          UtilsService.cleanTypenames(value.data.userTestDriveRequests)
        );
      } else {
        commit('userTestDriveRequests', []);
        EventService.emitRequestError(emit, value.error);
      }
    });
};

const rescheduleTestDriveBooking = async (
  { commit: _ }: GenericStore,
  testDriveBookingReschedule: TestDriveBookingRescheduleInput
): Promise<any> => {
  const variables: MutationCreateTestDriveBookingRescheduleArgs = {
    testDriveBookingReschedule,
  };

  return await client
    .mutation(changeTestDriveBooking, variables)
    .toPromise()
    .then((response) => {
      if (response.data) {
        return UtilsService.cleanTypenames(response);
      } else if (response.error) {
        throw new Error(
          `Server error: `,
          UtilsService.cleanTypenames(response.error)
        );
      }
    });
};

export const actions = {
  initUserTestDrives,
  getTestDriveBookingsCalendar,
  getTestDriveBookingsTimeSlots,
  getUserTestDriveBookings,
  getUserTestDriveRequests,
  rescheduleTestDriveBooking,
};
